import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { pePortfolioService } from '@/services/pePortfolioService';
import {
  type ProvisioningProfile,
  type ProvisioningProfileRole,
  type TemplateType,
  type ProvisionScope,
} from '@/services/provisioningProfileService';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { TrashIcon, ServerIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { TemplateSelectionModal } from '@/components/provisioning/TemplateSelectionModal';
import { RoleSelectionModal } from '@/components/provisioning/RoleSelectionModal';
import { ProvisioningTemplateTypeIconBar } from '@/components/provisioning/ProvisioningTemplateTypeIconBar';

type RoleTab = 'tenant' | 'tenantAdmin' | 'user';
type ProvisioningProfileSource = 'platform_default' | 'pe_firm_copy';

/**
 * Same as Admin → Provisioning profiles → **Tenant** segment (all three sub-tabs):
 * TENANT_ADMIN (Tenant + Tenant admin views) and TENANT_USER (User tab) for portfolio companies.
 */
export const PePortfolioProvisioningTab: React.FC = () => {
  const { t } = useLanguage();

  const [activeRoleTab, setActiveRoleTab] = useState<RoleTab>('tenant');
  const [profile, setProfile] = useState<ProvisioningProfile | null>(null);
  const [provisioningProfileSource, setProvisioningProfileSource] = useState<ProvisioningProfileSource | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [resetForkBusy, setResetForkBusy] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType | null>(null);
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  const resolvedProfileCode: 'TENANT_ADMIN' | 'TENANT_USER' = useMemo(
    () => (activeRoleTab === 'user' ? 'TENANT_USER' : 'TENANT_ADMIN'),
    [activeRoleTab]
  );

  /** Tenant tab = TENANT scope; Tenant admin = USER scope on admin profile; User tab = full TENANT_USER profile */
  const contentScopeFilter: 'ALL' | 'TENANT' | 'USER' = useMemo(() => {
    if (activeRoleTab === 'user') return 'ALL';
    if (activeRoleTab === 'tenant') return 'TENANT';
    return 'USER';
  }, [activeRoleTab]);

  const loadProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const { profile: p, provisioningProfileSource: src } =
        resolvedProfileCode === 'TENANT_USER'
          ? await pePortfolioService.getPePortfolioProvisioningProfile('TENANT_USER')
          : await pePortfolioService.getPePortfolioProvisioningProfile('TENANT_ADMIN', {
              tenantAdminView: activeRoleTab === 'tenantAdmin' ? 'tenantAdmin' : 'tenant',
            });
      setProfile(p);
      setProvisioningProfileSource(src);
      setCurrentProfileId(p.id);
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
      setProfile(null);
      setProvisioningProfileSource(null);
    } finally {
      setLoadingProfile(false);
    }
  }, [resolvedProfileCode, activeRoleTab]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const refreshAfterMutation = useCallback(async () => {
    await loadProfile();
  }, [loadProfile]);

  const handleDiscardFork = async () => {
    if (!window.confirm(t('provisioningProfiles.pePortfolioDiscardForkConfirm'))) return;
    try {
      setResetForkBusy(true);
      await pePortfolioService.deletePePortfolioProvisioningFork(resolvedProfileCode);
      toast.success(t('provisioningProfiles.pePortfolioForkDiscarded'));
      await loadProfile();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setResetForkBusy(false);
    }
  };

  const loadRolesForPePortfolio = useCallback(
    () =>
      pePortfolioService.getPePortfolioAvailableRoles(activeRoleTab === 'user' ? 'userProfile' : 'tenant'),
    [activeRoleTab]
  );

  const handleRemoveTemplate = async (_profileId: string, templateType: TemplateType, templateId: string) => {
    if (!confirm(t('provisioningProfiles.confirmRemove'))) return;
    try {
      await pePortfolioService.removePePortfolioTemplate(resolvedProfileCode, templateType, templateId);
      toast.success(t('provisioningProfiles.templateRemoved'));
      await refreshAfterMutation();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    }
  };

  const openTemplatePickerForType = (profileId: string, type: TemplateType) => {
    setCurrentProfileId(profileId);
    setSelectedTemplateType(type);
    setTemplateModalOpen(true);
  };

  const openRoleModal = (profileId: string) => {
    setCurrentProfileId(profileId);
    setRoleModalOpen(true);
  };

  const provisionScopeForTab = (): ProvisionScope => {
    if (activeRoleTab === 'tenant') return 'TENANT';
    if (activeRoleTab === 'tenantAdmin') return 'USER';
    return 'USER';
  };

  const handleTemplateSelected = async (
    templateId: string,
    templateName: string,
    isRequired: boolean,
    provisionOrder: number,
    modalProvisionScope: ProvisionScope
  ) => {
    if (!currentProfileId || !selectedTemplateType) return;
    const scope =
      activeRoleTab === 'user' ? modalProvisionScope : provisionScopeForTab();
    try {
      await pePortfolioService.associatePePortfolioTemplate(resolvedProfileCode, {
        templateType: selectedTemplateType,
        templateId,
        templateName,
        isRequired,
        provisionOrder,
        provisionScope: scope,
      });
      toast.success(t('provisioningProfiles.templateAssociated'));
      await refreshAfterMutation();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    }
  };

  const handleRoleSelected = async (roleId: string, provisionOrder: number, modalProvisionScope: ProvisionScope) => {
    if (!currentProfileId) return;
    const scope = activeRoleTab === 'user' ? modalProvisionScope : provisionScopeForTab();
    try {
      await pePortfolioService.associatePePortfolioRole(resolvedProfileCode, {
        roleId,
        provisionOrder,
        provisionScope: scope,
      });
      toast.success(t('provisioningProfiles.roleAssociated'));
      await refreshAfterMutation();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    }
  };

  const handleRemoveRole = async (_profileId: string, roleId: string) => {
    if (!confirm(t('provisioningProfiles.confirmRemoveRole'))) return;
    try {
      await pePortfolioService.removePePortfolioRole(resolvedProfileCode, roleId);
      toast.success(t('provisioningProfiles.roleRemoved'));
      await refreshAfterMutation();
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    }
  };

  const renderProfileContent = (p: ProvisioningProfile, scopeFilter: 'ALL' | 'TENANT' | 'USER') => {
    const allTemplates = p.templates || [];
    const tenantTemplates = allTemplates.filter((x) => (x.provision_scope || 'USER') === 'TENANT');
    const userTemplates = allTemplates.filter((x) => (x.provision_scope || 'USER') === 'USER');
    const filteredTemplates =
      scopeFilter === 'ALL' ? allTemplates : scopeFilter === 'TENANT' ? tenantTemplates : userTemplates;
    const allRoles: ProvisioningProfileRole[] = p.roles || [];
    const filteredRoles: ProvisioningProfileRole[] =
      scopeFilter === 'ALL'
        ? allRoles
        : allRoles.filter((r) => (r.provision_scope || 'USER') === (scopeFilter === 'TENANT' ? 'TENANT' : 'USER'));
    return (
      <div className="space-y-3">
        <div className="text-xs text-[var(--color-secondary-text)]">
          {t('provisioningProfiles.summaryTemplates', { count: filteredTemplates.length })}
          {scopeFilter !== 'ALL' && (
            <span className="ml-1">
              {scopeFilter === 'TENANT'
                ? t('provisioningProfiles.scopeSuffixTenant')
                : t('provisioningProfiles.scopeSuffixUser')}
            </span>
          )}
          {filteredRoles.length > 0 && (
            <span className="ml-2">{t('provisioningProfiles.summaryRoles', { count: filteredRoles.length })}</span>
          )}
        </div>

        <ProvisioningTemplateTypeIconBar
          onSelectTemplateType={(type) => openTemplatePickerForType(p.id, type)}
          onAddRoles={() => openRoleModal(p.id)}
          disabled={loadingProfile}
        />

        {filteredTemplates.length > 0 ? (
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-start justify-between p-2 bg-[var(--color-secondary-bg)] rounded border border-[var(--color-border)] text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap mb-1">
                    <span className="font-medium text-[var(--color-surface-text)] truncate">
                      {template.template_name || template.template_id}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {template.template_type}
                    </Badge>
                    <Badge variant={template.provision_scope === 'TENANT' ? 'success' : 'info'} className="text-xs">
                      {template.provision_scope === 'TENANT'
                        ? t('provisioningProfiles.tenant')
                        : t('provisioningProfiles.user')}
                    </Badge>
                    {template.is_required && (
                      <Badge variant="warning" className="text-xs">
                        {t('provisioningProfiles.required')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[var(--color-secondary-text)]">
                    {t('provisioningProfiles.orderLabel')} {template.provision_order}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleRemoveTemplate(p.id, template.template_type, template.template_id)}
                  className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-error)] ml-2 flex-shrink-0"
                  title={t('provisioningProfiles.removeTemplate')}
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-secondary-text)] text-center py-2">
            {t('provisioningProfiles.noTemplatesAssociated')}
          </p>
        )}

        <div className="border-t border-[var(--color-border)] pt-3">
          <h4 className="text-xs font-semibold text-[var(--color-surface-text)] mb-2">
            {t('provisioningProfiles.associatedRoles')}
          </h4>
          {filteredRoles.length > 0 ? (
            <div className="space-y-2">
              {filteredRoles.map((role: ProvisioningProfileRole) => (
                <div
                  key={role.id}
                  className="flex items-start justify-between p-2 bg-[var(--color-secondary-bg)] rounded border border-[var(--color-border)] text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-[var(--color-surface-text)]">
                        {role.display_name || role.role_name || role.role_id}
                      </span>
                      <Badge variant={role.provision_scope === 'TENANT' ? 'success' : 'info'} className="text-xs">
                        {role.provision_scope === 'TENANT'
                          ? t('provisioningProfiles.tenant')
                          : t('provisioningProfiles.user')}
                      </Badge>
                    </div>
                    <p className="text-[var(--color-secondary-text)]">
                      {t('provisioningProfiles.orderLabel')} {role.provision_order}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRemoveRole(p.id, role.role_id)}
                    className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-error)] ml-2 flex-shrink-0"
                    title={t('provisioningProfiles.removeRole')}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--color-secondary-text)] text-center py-2">
              {scopeFilter === 'ALL'
                ? t('provisioningProfiles.noRolesAssociated')
                : scopeFilter === 'TENANT'
                  ? t('provisioningProfiles.noRolesInViewTenant')
                  : t('provisioningProfiles.noRolesInViewUser')}
            </p>
          )}
        </div>
      </div>
    );
  };

  const tenantTabLabel = t('provisioningProfiles.tabTenant');
  const tenantAdminLabel = t('provisioningProfiles.tabTenantAdmin');
  const userLabel = t('provisioningProfiles.tabUser');

  const templateModalLockScope: ProvisionScope | undefined =
    activeRoleTab === 'tenant' ? 'TENANT' : activeRoleTab === 'tenantAdmin' ? 'USER' : undefined;

  const roleModalLockScope: ProvisionScope | undefined =
    activeRoleTab === 'tenant' ? 'TENANT' : activeRoleTab === 'tenantAdmin' ? 'USER' : undefined;

  if (loadingProfile) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-[var(--color-secondary-text)]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--color-info)] mb-3" />
        {t('provisioningProfiles.loading')}
      </div>
    );
  }

  if (!profile) {
    return (
      <p className="text-sm text-[var(--color-error)]">
        {t('provisioningProfiles.profileNotFound', { code: resolvedProfileCode })}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <div className="flex items-center gap-3">
          <ServerIcon className="w-6 h-6 text-[var(--color-surface-text)] shrink-0" />
          <div>
            <h2 className="text-xl font-bold text-[var(--color-surface-text)]">
              {t('provisioningProfiles.pePortfolioHeading')}
            </h2>
            <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
              {t('provisioningProfiles.pePortfolioDescription')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-3 bg-[var(--color-info)]/10 border border-[var(--color-info)]/20 rounded-lg">
        <h3 className="text-xs font-semibold text-[var(--color-info)] mb-1">
          {t('provisioningProfiles.scopeExplained')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-[var(--color-info)]">
          <div>
            <span className="font-medium">{t('provisioningProfiles.scope.tenantTitle')}</span>
            <p className="mt-1">{t('provisioningProfiles.scope.tenantBody')}</p>
          </div>
          <div>
            <span className="font-medium">{t('provisioningProfiles.scope.userTitle')}</span>
            <p className="mt-1">{t('provisioningProfiles.scope.userBody')}</p>
          </div>
          <div>
            <span className="font-medium">{t('provisioningProfiles.scope.adminTitle')}</span>
            <p className="mt-1">{t('provisioningProfiles.scope.adminBody')}</p>
          </div>
        </div>
      </div>

      {provisioningProfileSource === 'platform_default' && (
        <div
          className="p-3 rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 text-sm text-[var(--color-surface-text)]"
          role="status"
        >
          <p className="font-medium text-[var(--color-warning)]">
            {t('provisioningProfiles.pePortfolioInheritTitle')}
          </p>
          <p className="mt-1 text-xs text-[var(--color-secondary-text)]">
            {t('provisioningProfiles.pePortfolioInheritBody')}
          </p>
        </div>
      )}

      {provisioningProfileSource === 'pe_firm_copy' && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-secondary-bg)]">
          <p className="text-sm text-[var(--color-surface-text)]">
            {t('provisioningProfiles.pePortfolioFirmCopyActive')}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={resetForkBusy || loadingProfile}
            onClick={() => void handleDiscardFork()}
            className="shrink-0 inline-flex items-center gap-2"
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
            {t('provisioningProfiles.pePortfolioDiscardFork')}
          </Button>
        </div>
      )}

      <div className="mt-4">
        <div className="tab-bar">
          <nav className="flex gap-1 overflow-x-auto items-end" aria-label={t('provisioningProfiles.ariaSubTabs')}>
            <button
              type="button"
              onClick={() => setActiveRoleTab('tenant')}
              className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'tenant' ? 'tab-button-selected' : ''}`}
              aria-selected={activeRoleTab === 'tenant'}
            >
              {tenantTabLabel}
            </button>
            <button
              type="button"
              onClick={() => setActiveRoleTab('tenantAdmin')}
              className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'tenantAdmin' ? 'tab-button-selected' : ''}`}
              aria-selected={activeRoleTab === 'tenantAdmin'}
            >
              {tenantAdminLabel}
            </button>
            <button
              type="button"
              onClick={() => setActiveRoleTab('user')}
              className={`tab-button inline-flex items-center gap-2 ${activeRoleTab === 'user' ? 'tab-button-selected' : ''}`}
              aria-selected={activeRoleTab === 'user'}
            >
              {userLabel}
            </button>
          </nav>
        </div>
        <p className="mt-2 text-xs text-[var(--color-secondary-text)]">
          {activeRoleTab === 'tenant' && t('provisioningProfiles.tabHelpTenant')}
          {activeRoleTab === 'tenantAdmin' && t('provisioningProfiles.tabHelpTenantAdmin')}
          {activeRoleTab === 'user' && t('provisioningProfiles.tabHelpUser')}
        </p>
      </div>

      <div className="mt-6">{renderProfileContent(profile, contentScopeFilter)}</div>

      {currentProfileId && selectedTemplateType && (
        <TemplateSelectionModal
          isOpen={templateModalOpen}
          onClose={() => {
            setTemplateModalOpen(false);
            setSelectedTemplateType(null);
          }}
          profileId={currentProfileId}
          templateType={selectedTemplateType}
          onSelect={handleTemplateSelected}
          existingTemplateIds={profile?.templates?.map((x) => x.template_id) || []}
          lockProvisionScope={templateModalLockScope}
        />
      )}

      {currentProfileId && (
        <RoleSelectionModal
          isOpen={roleModalOpen}
          onClose={() => setRoleModalOpen(false)}
          profileId={currentProfileId}
          onSelect={handleRoleSelected}
          existingRoleIds={profile?.roles?.map((r) => r.role_id) || []}
          lockProvisionScope={roleModalLockScope}
          loadRolesOverride={loadRolesForPePortfolio}
        />
      )}
    </div>
  );
};
